import { useTranslation } from "react-i18next";
import { Breadcrumb } from "../../../general/modules/commons/breadcrumb/breadcrumb";
import { Content } from "../../../general/modules/commons/content/content";
import {
  Layout,
  LayoutContent,
  LayoutHeader,
  LayoutHeaderTitle,
} from "../../../general/modules/commons/layout/layout";
import {
  RelationModel,
  relationSchema,
} from "../../domain/models/RelationModel";
import { useParams } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../hooks/useTypedSelector";
import { logout } from "../../../auth/infrastructure/slices/authSlice/authSlice";
import useCrudForm from "../../../../hooks/useCrudForm";
import { RelationMutateNavigation } from "../features/RelationMutateNavigation";
import { RelationMutateFormTemplate } from "../commons/relationMutateFormTemplate/relationMutateFormTemplate";
import { CrudPlaceholderInput } from "../../../general/modules/commons/crud/inputs/crudPlaceholderInput";
import { BreadcrumbItem, Col, Form, Row } from "react-bootstrap";
import { CrudForm } from "../../../general/modules/commons/crud/crudForm";
import { ContentHeader } from "../../../general/modules/commons/content/contentHeader";
import React, { useState, useEffect } from "react";
import { getQrFieldOptions } from "../../infrastructure/slices/RelationSlice";
import { useAuthorization } from "../../../../hooks/useAuthorization";

export const RelationMutatePage = () => {
  const { t } = useTranslation("common");
  const { id } = useParams();
  const { hasRole } = useAuthorization();

  const [qrFieldOptions, setQrFieldOptions] = useState<string[]>([]);

  useEffect(() => {
    if (!qrFieldOptions.length) {
      const fetchQrFieldOptions = async () => {
        const response = await dispatch(getQrFieldOptions());
        setQrFieldOptions(response.payload as string[]);
      };
      void fetchQrFieldOptions();
    }
  }, []);

  const { loading } = useAppSelector((state) => state.crudMutate);
  const model = useAppSelector((state) => state.crudMutate)
    .model as RelationModel;

  const dispatch = useAppDispatch();

  const title = model.id
    ? t("relation.mutate.edit", { name: model.name })
    : t("relation.mutate.create");

  const [checkedState, setCheckedState] = useState<{
    [key: string]: boolean;
  }>({});

  const handleCheckboxChange = (name: string, isChecked: boolean) => {
    setCheckedState((prevState) => ({
      ...prevState,
      [name]: isChecked,
    }));
  };

  useEffect(() => {
    const checkedBoxes = model.tireset_sticker_config || [];
    const initialCheckedState: { [key: string]: boolean } = {};
    checkedBoxes.forEach((box) => {
      initialCheckedState[box] = true;
    });
    setCheckedState(initialCheckedState);
  }, [model.tireset_sticker_config]);

  const selectOptions: {
    invoice_type: { value: string; label: string; isDisabled: boolean }[];
  } = {
    invoice_type: [
      {
        value: "pickedUp",
        label: t("Picked up"),
        isDisabled: false,
      },
      {
        value: "delivered",
        label: t("Delivered"),
        isDisabled: false,
      },
      {
        value: "other",
        label: t("Other"),
        isDisabled: false,
      },
    ],
  };

  const crud = useCrudForm({
    schema: relationSchema,
    prefix: "relations",
    createMutation: "upsertRelation",
    updateMutation: "upsertRelation",
    manageFields: ["only_freestock", "invoice_cleaning"],
    disabledFields: [
      hasRole("dealer-employee") ? "name" : undefined,
      hasRole("dealer-employee") ? "street" : undefined,
      hasRole("dealer-employee") ? "house_number" : undefined,
      hasRole("dealer-employee") ? "house_number_addition" : undefined,
      hasRole("dealer-employee") ? "postal_code" : undefined,
      hasRole("dealer-employee") ? "city" : undefined,
      hasRole("dealer-employee") ? "country" : undefined,
      hasRole("dealer-employee") ? "phone" : undefined,
    ],
    hiddenFields: [
      hasRole("dealer-employee") ? "dbb_api_identifier" : undefined,
      hasRole("dealer-employee") ? "invoice_type" : undefined,
      hasRole("dealer-employee") ? "debtor_number" : undefined,
      hasRole("dealer-employee") ? "invoice_product" : undefined,
      hasRole("dealer-employee") ? "status" : undefined,
      hasRole("dealer-employee") ? "invoice_cleaning" : undefined,
      hasRole("dealer-employee") ? "only_freestock" : undefined,
    ],
    mutationExclude: [
      hasRole("dealer-employee") ? "dbb_api_identifier" : "",
      hasRole("dealer-employee") ? "invoice_type" : "",
      hasRole("dealer-employee") ? "debtor_number" : "",
      hasRole("dealer-employee") ? "invoice_product" : "",
      hasRole("dealer-employee") ? "status" : "",
      hasRole("dealer-employee") ? "invoice_cleaning" : "",
      hasRole("dealer-employee") ? "only_freestock" : "",
    ],
    id: id ? parseInt(id) : undefined,
    formTemplate: RelationMutateFormTemplate,
    onBeforeSubmit: (values) => {
      const checkedJSON = JSON.stringify(
        Object.entries(checkedState).reduce(
          (acc, [key, value]) => (value ? [...acc, key] : acc),
          [] as string[],
        ),
      );

      return {
        ...values,
        tireset_sticker_config: checkedJSON,
      };
    },
    selectOptions: selectOptions,
  });

  if (Object.keys(model).length < 1 && id && !loading)
    return (
      <Layout>
        <LayoutContent>
          <Content>
            <ContentHeader className={"mt-3"} title={t("Access denied")} />
            <p className={"mt-3"}>{t("You don't have access to this page")}</p>
          </Content>
        </LayoutContent>
      </Layout>
    );

  if (
    hasRole("dbh-employee") ||
    hasRole("dbh-driver") ||
    hasRole("dbh-order-picker")
  ) {
    return (
      <Layout>
        <LayoutContent>
          <Content>
            <ContentHeader className={"mt-3"} title={t("Access denied")} />
            <p className={"mt-3"}>{t("You don't have access to this page")}</p>
          </Content>
        </LayoutContent>
      </Layout>
    );
  }

  return (
    <Layout
      header={
        <LayoutHeader logout={() => void dispatch(logout())}>
          <LayoutHeaderTitle title={title} />
          <Breadcrumb>
            <BreadcrumbItem>{title}</BreadcrumbItem>
          </Breadcrumb>
        </LayoutHeader>
      }
    >
      <LayoutContent>
        {id && <RelationMutateNavigation />}
        <Content>{crud.form}</Content>
        {!hasRole("dealer-employee") && (
          <div className={"col-md-3 col-lg-4"}>
            <Content>
              {crud.manageHeader}
              {crud.manage}
            </Content>
            {crud.logs}
          </div>
        )}
      </LayoutContent>

      {!hasRole("dealer-employee") && (
        <LayoutContent>
          <Content md={6} lg={4}>
            <ContentHeader title={t("crud.sticker.title")} className="mb-3" />
            <Col>
              <CrudForm
                schema={relationSchema}
                prefix={"relations"}
                form={crud.formLogic}
                formTemplate={() => (
                  <Row>
                    <Col md="12">
                      <CrudPlaceholderInput name="sticker_amount" />
                    </Col>
                  </Row>
                )}
                disabledFields={undefined}
              />
              <Col>
                <div className="mb-3">{t("crud.sticker.options") + ":"}</div>
                <Row className="flex-column">
                  {qrFieldOptions.map((option, i) => (
                    <Col key={i}>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          id={i + ""}
                          key={i + ""}
                          name={option}
                          label={t(`crud.sticker.options.${option}`)}
                          checked={checkedState[option] || false}
                          onChange={(e) =>
                            handleCheckboxChange(option, e.target.checked)
                          }
                          isInvalid={false}
                        />
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Col>
          </Content>
        </LayoutContent>
      )}
    </Layout>
  );
};
